/**
 * Who is signed in, and what they may do.
 *
 * Permissions come from the server rather than being derived from the role
 * here; a second copy of that mapping would drift and show buttons that 403.
 *
 * `loading` keeps a reload from flashing the sign-in screen, and `offline`
 * keeps "the API did not answer" from being read as "you are not signed in".
 * Retry and backoff live on the session query in `queries/auth.ts`.
 */

import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiOffline } from '../api/errors';
import {
	useSessionQuery,
	useSignInMutation,
	useSignOutMutation,
	useVocabularyQuery,
} from '../queries/auth';
import { keys } from '../queries/keys';
import type { Permissions, Session, Term, User, Vocabulary } from '../schemas';

interface AuthState {
	user: User | null;
	permissions: Permissions | null;
	/** The words for every status the API returns. The UI never invents a label. */
	vocabulary: Vocabulary | null;
	loading: boolean;
	/** Set when the API could not be reached at all. Not a signed-out state. */
	offline: ApiOffline | null;
	signIn: (email: string, password: string) => Promise<void>;
	signOut: () => Promise<void>;
	reload: () => Promise<void>;
	/** Adopt the session that register returned, without a second round trip. */
	adopt: (session: Session) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
	const client = useQueryClient();
	const session = useSessionQuery();
	const vocabulary = useVocabularyQuery(Boolean(session.data));
	const signInMutation = useSignInMutation();
	const signOutMutation = useSignOutMutation();

	/* `failureReason` too: `error` is only set once retries are exhausted, so
     on its own the offline screen would take ~30s to appear. */
	const failure = session.error ?? session.failureReason;
	const offline = failure instanceof ApiOffline ? failure : null;

	/* Released on an answer or on a reason there is not one, so the offline
     screen appears instead of hanging for the length of the backoff. */
	const loading = session.isPending && !offline;

	const adopt = useCallback(
		(next: Session) => {
			client.setQueryData(keys.auth.session(), next);
		},
		[client],
	);

	const signIn = useCallback(
		async (email: string, password: string) => {
			await signInMutation.mutateAsync({ email, password });
		},
		[signInMutation],
	);

	const signOut = useCallback(async () => {
		await signOutMutation.mutateAsync();
	}, [signOutMutation]);

	const reload = useCallback(async () => {
		await session.refetch();
	}, [session]);

	const value = useMemo<AuthState>(
		() => ({
			user: session.data?.user ?? null,
			permissions: session.data?.permissions ?? null,
			vocabulary: vocabulary.data ?? null,
			loading,
			offline,
			signIn,
			signOut,
			reload,
			adopt,
		}),
		[
			session.data,
			vocabulary.data,
			loading,
			offline,
			signIn,
			signOut,
			reload,
			adopt,
		],
	);

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
}

export function useAuth(): AuthState {
	const value = useContext(AuthContext);
	if (!value) throw new Error('useAuth must be used inside AuthProvider');
	return value;
}

/**
 * The words for one enum value: the server's term, or a readable fallback
 * built from the value so an unknown status still renders as prose.
 */
export function useTerm(
	group: keyof Vocabulary,
	value: string | null | undefined,
): Term {
	const { vocabulary } = useAuth();
	if (!value) return { label: '—', tone: 'neutral' };
	return (
		vocabulary?.[group]?.[value] ?? {
			label:
				value.charAt(0) +
				value.slice(1).toLowerCase().replace(/_/g, ' '),
			tone: 'neutral',
		}
	);
}
